// @flow
import React, { useEffect, useState, useCallback } from 'react'
import { Meteor } from 'meteor/meteor'
import moment from 'moment'
import useReactRouter from 'use-react-router'
import { toast } from 'react-toastify'

import makeStyles from '@material-ui/styles/makeStyles/makeStyles'

import TextInput from '../components/TextInput'
import Button from '../components/Button'
import Select from '../components/Select'
import DatePicker from '../components/DatePicker'
import PhoneInput from '../components/PhoneInput'
import ButtonDeleteProfile from '../components/ButtonDeleteProfile'
import Page from '../components/Page'
import CustomLabel from '../components/CustomLabel'
import trackEvent from '../lib/trackEvent'

import { useGlobalState } from '../store'
import { formattedMessage } from '../CurrentLocale'
import { getNextPage } from '../routeWorkflow'
import { validate } from '../lib/validate.js'
import { openAlertDialog, sendToSlack } from '../fonction'
import LoadingPage from './LoadingPage'
import { fetchMe, fetchUsers } from '../lib/user'

const initialState = {
  phoneError: false,
  firstnameError: false,
  lastnameError: false,
  zipCodeError: false,
  birthdayError: false,
  customerTypeError: false,
  phoneValid: false,
  firstnameValid: false,
  lastnameValid: false,
  zipCodeValid: false,
  birthdayValid: false,
  customerTypeValid: false,
}

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
  },
  wrapper: { flex: '1 0 50%' },
})

const ProfileSubAccount = () => {
  const {
    history,
    location: { state: { userId = '' } = {} },
  } = useReactRouter()

  const [{ profile, profileUser }] = useGlobalState('user')
  const [{ listCustomerType }] = useGlobalState('customerType')
  const classes = useStyles()
  const [{ phoneValue, country, phoneExist }, setPhoneExist] = useState({
    phoneValue: '',
    country: 'FR',
    phoneExist: '',
  })

  const [profileUserSelect, setProfileUserSelect] = useState(profile)
  const [loading, setLoading] = useState(true)
  const [loadingSave, setLoadingSave] = useState(false)

  useEffect(() => {
    fetchUsers(userId)
  }, [userId])

  const fetchUserSelect = useCallback(() => {
    if (userId === '') {
      setProfileUserSelect({
        firstname: '',
        lastname: '',
        email: '',
        zipCode: '',
        birthday: null,
        validated: false,
        type: 'customer',
        customerType: '',
        phone: '',
        offerCode: '',
        callPartnerCode: '',
      })
    } else if (!!userId && !!profileUser && !!profileUser[userId]) {
      setProfileUserSelect(profileUser[userId])
    }
    setLoading(false)
  }, [profileUser, userId])

  useEffect(() => {
    fetchUserSelect()
  }, [])

  const [
    {
      phoneError,
      firstnameError,
      lastnameError,
      zipCodeError,
      birthdayError,
      customerTypeError,
      phoneValid,
      firstnameValid,
      lastnameValid,
      zipCodeValid,
      birthdayValid,
      customerTypeValid,
    },
    setControl,
  ] = useState(initialState)

  if (loading || listCustomerType.length === 0) return <LoadingPage />

  const {
    firstname = '',
    lastname = '',
    email = '',
    zipCode = '',
    birthday = null,
    validated = false,
    customerType,
    phone,
  } = profileUserSelect

  const handleSubmit = (): void => {
    setControl(initialState) // Reinit error

    const constraints = {
      firstname: {
        presence: { message: `^${formattedMessage({ id: 'ProfileForm.validatorFirstname' })}\n` },
        length: {
          minimum: 1,
          message: `^${formattedMessage({ id: 'ProfileForm.validatorFirstname' })}\n`,
        },
      },
      lastname: {
        presence: { message: `^${formattedMessage({ id: 'ProfileForm.validatorLastname' })}\n` },
        length: {
          minimum: 1,
          message: `^${formattedMessage({ id: 'ProfileForm.validatorLastname' })}\n`,
        },
      },
      birthday: {
        presence: { message: `^${formattedMessage({ id: 'ProfileForm.validatorBirthDay' })}\n` },
        datetime: {
          latest: moment().subtract(18, 'years').toISOString(),
          message: `^${formattedMessage({ id: 'ProfileForm.validatorBirthDay2' })}\n`,
        },
      },
    }

    const control =
      validate({ firstname, lastname, birthday }, constraints, {
        format: 'detailed',
      }) || []

    if (customerType === '') {
      control.push({
        attribute: 'customerType',
        error: `${formattedMessage({ id: 'ProfileForm.validatorCustomerType' })}\n`,
      })
    }
    if (zipCode === '') {
      control.push({
        attribute: 'zipCode',
        error: `${formattedMessage({ id: 'ProfileForm.validatorzipCode' })}\n`,
      })
    }

    if (phoneExist === 'existCompte') {
      control.push({
        attribute: 'phone',
        error: `${formattedMessage({ id: 'ProfileForm.validatorAccountAlreadyExist' })}\n`,
      })
    } else if (phoneExist === 'deleteCompte') {
      control.push({
        attribute: 'phone',
        error: `${formattedMessage({ id: 'ProfileForm.validatorAccountDeleted' })}\n`,
      })
      sendToSlack(
        'users',
        `Le numéro de téléphone ${phone} dont le compte a été supprimé, essaye de se réinscrire`
      )
    } else if (phoneExist !== 'newCompte') {
      control.push({
        attribute: 'phone',
        error: `${formattedMessage({ id: 'ProfileForm.validatorPhone' })}\n`,
      })
    }

    if (!!control && control.length > 0) {
      const inputError = control.map((item) => `${item.attribute}Error`)
      setControl({
        phoneError: inputError.includes('phoneError'),
        firstnameError: inputError.includes('firstnameError'),
        lastnameError: inputError.includes('lastnameError'),
        zipCodeError: inputError.includes('zipCodeError'),
        birthdayError: inputError.includes('birthdayError'),
        customerTypeError: inputError.includes('customerTypeError'),
        phoneValid: !inputError.includes('phoneValid'),
        firstnameValid: !inputError.includes('firstnameValid'),
        lastnameValid: !inputError.includes('lastnameValid'),
        zipCodeValid: !inputError.includes('zipCodeValid'),
        birthdayValid: !inputError.includes('birthdayValid'),
        customerTypeValid: !inputError.includes('customerTypeValid'),
      })

      openAlertDialog({
        message: control.map((item) => item.error).join(''),
        title: formattedMessage({ id: 'global.error' }),
      })
    } else {
      setLoadingSave(true)

      const newProfile = userId
        ? { ...profileUserSelect, userId }
        : { ...profileUserSelect, newUserChildrenPro: true }

      Meteor.promise('updateUser', newProfile)
        .then(() => {
          userId ? fetchUsers(userId) : fetchMe()
          toast.success(formattedMessage({ id: 'ProfileForm.profileUpdated' }))
          setLoadingSave(false)

          trackEvent('UPDATE_PROFILE', newProfile)

          history.push('/my-sub-account')
        })
        .catch((error) => {
          setLoadingSave(false)
          console.error(error)
          toast.error(formattedMessage({ id: 'error' }) + formattedMessage({ id: error.error }))
        })
    }
  }

  const handleChange = (target: string) => (value: string) => {
    if (target === 'birthday') {
      value = moment(value, moment.localeData().longDateFormat('L')).toISOString()
    }

    setProfileUserSelect({
      ...profileUserSelect,
      [target]: value,
    })
  }

  const testPhoneExiste = (getState): void => {
    setPhoneExist({ phoneValue, country, phoneExist: getState.phoneExist })

    setProfileUserSelect({
      ...profileUserSelect,
      phone: getState.phoneValue,
      regionCode: getState.country,
    })
  }

  // --- Calculate calltoaction
  const callToAction = []

  validated &&
    callToAction.push(
      <ButtonDeleteProfile loading={loadingSave} validated={validated} pro={true} />
    )

  callToAction.push(
    <Button
      loading={loadingSave}
      onClick={() => history.goBack()}
      title={formattedMessage({ id: 'cancel' })}
      variant="contained"
      color="secondary"
    />
  )

  callToAction.push(
    <Button
      loading={loadingSave}
      onClick={() => handleSubmit()}
      title={formattedMessage({ id: 'save' })}
      testId="button-saveProfile"
    />
  )

  return (
    <Page title={formattedMessage({ id: 'ProfileForm.titlepro' })} callToAction={callToAction}>
      <div className={classes.form}>
        <div className={classes.wrapper}>
          <CustomLabel>{formattedMessage({ id: 'LoginForm.phone' })}</CustomLabel>
          <PhoneInput
            inputError={phoneError}
            inputValid={phoneValid}
            phone={phoneValue}
            countryProps={country}
            userType={'B2B'}
            testPhoneExiste={testPhoneExiste}
          />
        </div>
        <div className={classes.wrapper}>
          <TextInput
            label={formattedMessage({ id: 'ProfileForm.firstname' })}
            valueInput={firstname}
            inputError={firstnameError}
            inputValid={firstnameValid}
            onChangeValue={handleChange('firstname')}
            testId="firstname-input"
          />
        </div>
        <div className={classes.wrapper}>
          <TextInput
            label={formattedMessage({ id: 'ProfileForm.lastname' })}
            valueInput={lastname}
            inputError={lastnameError}
            inputValid={lastnameValid}
            onChangeValue={handleChange('lastname')}
            testId="lastname-input"
          />
        </div>
        <div className={classes.wrapper}>
          <TextInput
            label={formattedMessage({ id: 'ProfileForm.email' })}
            valueInput={email}
            onChangeValue={handleChange('email')}
            testId="email-input"
            disabled={userId !== ''}
          />
        </div>
        <div className={classes.wrapper}>
          <TextInput
            label={formattedMessage({ id: 'ProfileForm.zipCode' })}
            valueInput={zipCode}
            inputError={zipCodeError}
            inputValid={zipCodeValid}
            onChangeValue={handleChange('zipCode')}
            testId="zipCode-input"
          />
        </div>
        <div className={classes.wrapper}>
          <DatePicker
            label={formattedMessage({ id: 'ProfileForm.birthday' })}
            valueDate={birthday}
            birthdayMode={true}
            inputError={birthdayError}
            inputValid={birthdayValid}
            onChangeDate={handleChange('birthday')}
            testId="birthday-input"
          />
        </div>
        <div className={classes.wrapper}>
          <Select
            optionsList={listCustomerType}
            handleSelectValue={handleChange('customerType')}
            selectedValue={customerType}
            inputError={customerTypeError}
            inputValid={customerTypeValid}
            label={formattedMessage({ id: 'ProfileForm.customerTypeLabel' })}
            testId="customerType-input"
          />
        </div>
      </div>
    </Page>
  )
}

export default ProfileSubAccount
